import { NOTE_COLORS } from "@sebas/core";
import { addHours, format } from "date-fns";
import { Alert, Modal } from "@hakistudio/hakiui";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  RECURRENCE_PRESETS, useCreateEvent, useDeleteEvent, useUpdateEvent,
  type Calendar, type Occurrence,
} from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { Input, Select } from "@/components/ui/Field";

/** <input type="datetime-local"> wants local wall-clock, not an ISO instant. */
const toLocalInput = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");

const DOT: Record<string, string> = {
  sand: "bg-tile-sand", coral: "bg-tile-coral", amber: "bg-tile-amber",
  mint: "bg-tile-mint", sky: "bg-tile-sky", lilac: "bg-tile-lilac",
};

export function EventDialog({
  calendars,
  occurrence,
  defaultDate,
  onClose,
}: {
  calendars: Calendar[];
  occurrence: Occurrence | null;
  defaultDate?: Date;
  onClose: () => void;
}) {
  const editing = Boolean(occurrence);
  const base = defaultDate ?? new Date();

  const [title, setTitle] = useState(occurrence?.title ?? "");
  const [location, setLocation] = useState(occurrence?.location ?? "");
  const [calendarId, setCalendarId] = useState(occurrence?.calendarId ?? calendars[0]!.id);
  const [color, setColor] = useState(occurrence?.color ?? "sky");
  const [allDay, setAllDay] = useState(occurrence?.allDay ?? false);
  const [rrule, setRrule] = useState(occurrence?.rrule ?? "");
  const [starts, setStarts] = useState(toLocalInput(occurrence ? occurrence.start : base));
  const [ends, setEnds] = useState(toLocalInput(occurrence ? occurrence.end : addHours(base, 1)));
  const [error, setError] = useState<string | null>(null);

  const create = useCreateEvent();
  const update = useUpdateEvent();
  const remove = useDeleteEvent();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // new Date() on a datetime-local string parses it as local time, which is
    // what the user meant; toISOString then converts to the UTC instant we store.
    const startsAt = new Date(starts);
    const endsAt = new Date(ends);
    if (endsAt < startsAt) {
      setError("The end time is before the start time.");
      return;
    }

    const payload = {
      calendarId,
      title: title.trim(),
      location: location.trim() || null,
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      allDay,
      rrule: rrule || null,
      color,
    };
    if (!payload.title) {
      setError("Give the event a title.");
      return;
    }

    const opts = { onSuccess: onClose, onError: (e: Error) => setError(e.message) };
    if (occurrence) update.mutate({ id: occurrence.id, ...payload }, opts);
    else create.mutate(payload, opts);
  };

  return (
    <Modal isOpen onClose={onClose} title={editing ? "Edit event" : "New event"}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {occurrence?.isRecurring ? (
          <p className="rounded-2xl bg-sunken px-3 py-2 text-xs text-ink-muted">
            This event repeats. Changes apply to the whole series.
          </p>
        ) : null}

        <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <Input placeholder="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} />

        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-4 w-4 accent-[var(--sb-accent)]" />
          All day
        </label>

        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Starts"
            type="datetime-local"
            value={starts}
            onChange={(e) => setStarts(e.target.value)}
          />
          <Input
            label="Ends"
            type="datetime-local"
            value={ends}
            onChange={(e) => setEnds(e.target.value)}
          />
        </div>

        <Select
          label="Repeats"
          value={rrule}
          onChange={setRrule}
          options={[
            ...RECURRENCE_PRESETS.map((p) => ({ label: p.label, value: p.value })),
            // Preserve a hand-written rule the presets do not cover.
            ...(rrule && !RECURRENCE_PRESETS.some((p) => p.value === rrule)
              ? [{ label: "Custom rule", value: rrule }]
              : []),
          ]}
        />

        <Select
          label="Calendar"
          value={calendarId}
          onChange={setCalendarId}
          options={calendars.map((c) => ({ label: c.name, value: c.id }))}
        />

        <div className="flex gap-2">
          {NOTE_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Colour ${c}`}
              onClick={() => setColor(c)}
              className={cn("h-6 w-6 rounded-full ring-offset-2 ring-offset-raised", DOT[c], color === c && "ring-2 ring-ink")}
            />
          ))}
        </div>

        {error ? <Alert variant="danger" title={error} /> : null}

        <div className="mt-1 flex items-center gap-2">
          <Button type="submit" className="flex-1" disabled={create.isPending || update.isPending}>
            {editing ? "Save" : "Create"}
          </Button>
          {editing ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label="Delete event"
              onClick={() => {
                if (confirm("Delete this event? A repeating event is removed entirely.")) {
                  remove.mutate(occurrence!.id, { onSuccess: onClose });
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
